import { AuthGuard, useLoginContext } from '@tiffinger-thiel/appauth-react';
import { BaseEmployeeDTOPreferredPortalEnum, BenefitDTOBenefitEnum } from 'probonio-shared-ui/api';
import { consumePendingLoginHint, loginHintExtras } from 'probonio-shared-ui/module/auth';
import { getWafBlockedMessageKey } from 'probonio-shared-ui/module/error';
import { MeInitializationResult } from 'probonio-shared-ui/module/me/meSlice';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingLayout } from '../../component/layout/LoadingLayout';
import { FEATURE_ONEPORTAL } from '../../constants';
import AccountMergedPage from '../../page/AccountMergedPage';
import InactivePage from '../../page/InactivePage';
import IncompleteProfilePage from '../../page/IncompleteProfilePage';
import MaintenancePage from '../../page/MaintenancePage';
import RegistrationPendingPage from '../../page/RegistrationPendingPage';
import WrongPreferredPortalPage from '../../page/WrongPreferredPortalPage';
import { useAppSelector } from '../../redux/hooks';
import { BenefitGuard } from '../benefit/BenefitGuard';
import { ErrorFallback } from '../error';
import { LegalTermsGuard } from '../legalTerms/LegalTermsGuard';

interface Props {
  page: React.ComponentType;
  benefit?: BenefitDTOBenefitEnum;
  disableToSGuard?: boolean;
}

export const OAUTH_RETURN_PATH = 'oauthReturnPath';

/**
 * Wraps a page component with a guard that automatically redirects to the login page
 */
const PrivatePage: React.FC<Props> = ({ page, benefit, disableToSGuard }) => {
  const { t } = useTranslation('errorModule');
  const { isReady, isLoggedIn, login } = useLoginContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isReady && !isLoggedIn) {
      localStorage.setItem(OAUTH_RETURN_PATH, `${location.pathname}${location.search}`);
      void login(loginHintExtras(consumePendingLoginHint()));
    }
  }, [isReady, isLoggedIn, login, location]);

  const handleLogout = useCallback(() => {
    navigate('/logout', { replace: true });
  }, [navigate]);

  const PageComponent = page;

  const { employment, initializationResult, wafCauseId } = useAppSelector(state => state.me);
  const wrongPreferredPortal =
    FEATURE_ONEPORTAL && employment && employment.preferredPortal === BaseEmployeeDTOPreferredPortalEnum.Bikeleasing;

  if (!initializationResult) {
    return <LoadingLayout />;
  } else if (initializationResult === MeInitializationResult.MAINTENANCE) {
    return <MaintenancePage logout={handleLogout} />;
  } else if (initializationResult === MeInitializationResult.ACCOUNT_MERGED) {
    return <AccountMergedPage />;
  } else if (
    initializationResult === MeInitializationResult.USER_NOT_FOUND ||
    initializationResult === MeInitializationResult.NO_VALID_EMPLOYMENT
  ) {
    return <InactivePage />;
  } else if (wrongPreferredPortal) {
    return <WrongPreferredPortalPage />;
  } else if (initializationResult === MeInitializationResult.REGISTRATION_PENDING) {
    return <RegistrationPendingPage />;
  } else if (initializationResult === MeInitializationResult.API_ERROR) {
    return <ErrorFallback logout={handleLogout} />;
  } else if (initializationResult === MeInitializationResult.WAF_BLOCKED) {
    return <ErrorFallback logout={handleLogout} message={t(getWafBlockedMessageKey(wafCauseId))} eventId={wafCauseId} />;
  } else if (initializationResult === MeInitializationResult.NEEDS_TO_COMPLETE_PROFILE) {
    return <IncompleteProfilePage />;
  } else {
    const page = disableToSGuard ? (
      <PageComponent />
    ) : (
      <LegalTermsGuard>
        <PageComponent />
      </LegalTermsGuard>
    );
    return <AuthGuard>{benefit ? <BenefitGuard benefit={benefit}>{page}</BenefitGuard> : page}</AuthGuard>;
  }
};

export default PrivatePage;
